<template>
  <a-card class="reference-parameters" :bordered="true">
    <div class="reference-parameters__top">
      <h3 class="reference-parameters__title">
        Лучших результатов добиваются сотрудники с данными параметрами ЭИ
      </h3>

      <div class="reference-parameters__description">
        <p>
          Для каждой из 17 способностей ЭИ представлены два значения:
        </p>

        <ol>
          <li>
            Первое (слева) - критически допустимая нижняя граница по баллам по этой способности.
          </li>

          <li>
            Второе (справа) - рекомендуемая граница по этой способности.
          </li>
        </ol>
      </div>

      <label class="reference-parameters__switch">
        <a-switch
            size="small"
            :checked="showUnimportant"
            @change="$emit('update:show-unimportant', $event)"
        />

        <span>Отображать неважные параметры</span>
      </label>
    </div>

    <div class="reference-parameters__scheme">
      <div v-if="totalCard" class="reference-parameters__total">
        <div class="reference-parameters__total-card">
          <ReferenceParameterCard
              :card="totalCard"
              class="reference-parameters__card"
          />
        </div>
      </div>

      <div
          v-if="visibleDomains.length"
          class="reference-parameters__domains"
      >
        <div
            v-for="(domain, domainIndex) in visibleDomains"
            :key="domain.name"
            class="reference-parameters__domain"
            :class="getDomainClasses(domain)"
        >
          <div
              v-if="domain.card"
              class="reference-parameters__domain-row"
          >
            <div
                class="reference-parameters__node reference-parameters__node_domain"
                :class="getDomainNodeClasses(domain, domainIndex)"
            >
              <ReferenceParameterCard
                  :card="domain.card"
                  class="reference-parameters__card"
              />
            </div>
          </div>

          <div
              v-if="domain.branches.length"
              class="reference-parameters__branches"
              :style="getBranchGridStyle(domain)"
          >
            <div
                v-for="branch in domain.branches"
                :key="branch.name"
                class="reference-parameters__branch-column"
            >
              <div
                  v-if="branch.card"
                  class="reference-parameters__node reference-parameters__node_branch"
                  :class="getBranchNodeClasses(domain, branch, domainIndex)"
              >
                <ReferenceParameterCard
                    :card="branch.card"
                    class="reference-parameters__card"
                />
              </div>

              <div
                  v-if="branch.sections.length"
                  class="reference-parameters__sections"
              >
                <div
                    v-for="(section, sectionIndex) in branch.sections"
                    :key="section.key"
                    class="reference-parameters__node reference-parameters__node_section"
                    :class="getSectionNodeClasses(domain, branch, sectionIndex, domainIndex)"
                >
                  <ReferenceParameterCard
                      :card="section"
                      class="reference-parameters__card"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <a-button
        data-html2canvas-ignore
        class="primary-button reference-parameters__staffs"
        @click="$emit('show-staffs')"
    >
      Смотреть подходящих сотрудников
    </a-button>
  </a-card>
</template>

<script>
import { computed } from "vue";
import ReferenceParameterCard from "./ReferenceParameterCard.vue";

const LEVELS = {
  TOTAL: "total",
  DOMAIN: "domain",
  BRANCH: "branch",
  SECTION: "section",
};

const TYPES = {
  EXPERIENCED: "experienced",
  STRATEGIC: "strategic",
};

const DEFAULT_DOMAIN_TYPE = "neutral";

export default {
  name: "ReferenceParametersBlock",

  components: {
    ReferenceParameterCard,
  },

  props: {
    cards: {
      type: [Map, Object, Array],
      required: true,
    },

    showUnimportant: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["update:show-unimportant", "show-staffs"],

  setup(props) {
    const normalizedCards = computed(() => {
      return normalizeCardEntries(props.cards)
          .map(([key, card], index) => normalizeCard(key, card, index))
          .filter((card) => card.level)
          .sort(sortByOrder);
    });

    const hierarchy = computed(() => {
      return buildHierarchy(normalizedCards.value);
    });

    const totalCard = computed(() => {
      return hierarchy.value.totalCard;
    });

    const visibleDomains = computed(() => {
      return hierarchy.value.domains;
    });

    const hasVisibleDomainCards = computed(() => {
      return visibleDomains.value.some((domain) => Boolean(domain.card));
    });

    function normalizeCardEntries(cards) {
      if (cards instanceof Map) {
        return Array.from(cards.entries());
      }

      if (Array.isArray(cards)) {
        return cards.map((card, index) => [card.key ?? String(index), card]);
      }

      if (cards && typeof cards === "object") {
        return Object.entries(cards);
      }

      return [];
    }

    function normalizeCard(key, card, index) {
      return {
        ...card,
        key,
        level: normalizeLevel(card.level),
        type: normalizeType(card.type),
        order: getCardOrder(card, index),
        visible: isCardVisible(card),
      };
    }

    function normalizeLevel(level) {
      return Object.values(LEVELS).includes(level) ? level : null;
    }

    function normalizeType(type) {
      return Object.values(TYPES).includes(type) ? type : DEFAULT_DOMAIN_TYPE;
    }

    function getCardOrder(card, index) {
      return typeof card.graphic_order === "number"
          ? card.graphic_order
          : index;
    }

    function isCardVisible(card) {
      return props.showUnimportant || card.important !== false;
    }

    function sortByOrder(left, right) {
      return left.order - right.order;
    }

    function buildHierarchy(cards) {
      const totalCard = cards.find((card) => {
        return card.level === LEVELS.TOTAL && card.visible;
      }) ?? null;

      const domainMap = new Map();

      cards.forEach((card) => {
        if (!card.visible || card.level === LEVELS.TOTAL) {
          return;
        }

        if (card.level === LEVELS.DOMAIN) {
          addDomainCard(domainMap, card);
          return;
        }

        if (card.level === LEVELS.BRANCH) {
          addBranchCard(domainMap, card);
          return;
        }

        if (card.level === LEVELS.SECTION) {
          addSectionCard(domainMap, card);
        }
      });

      return {
        totalCard,
        domains: getSortedDomains(domainMap),
      };
    }

    function addDomainCard(domainMap, card) {
      const domainName = getDomainName(card);

      if (!domainName) {
        return;
      }

      const domain = ensureDomain(domainMap, domainName, card);

      domain.card = card;
      domain.type = card.type;
      domain.order = Math.min(domain.order, card.order);
    }

    function addBranchCard(domainMap, card) {
      const domainName = getDomainName(card);
      const branchName = getBranchName(card);

      if (!domainName || !branchName) {
        return;
      }

      const domain = ensureDomain(domainMap, domainName, card);
      const branch = ensureBranch(domain, branchName);

      domain.order = Math.min(domain.order, card.order);
      branch.card = card;
      branch.order = Math.min(branch.order, card.order);
    }

    function addSectionCard(domainMap, card) {
      const domainName = getDomainName(card);
      const branchName = getBranchName(card);

      if (!domainName || !branchName) {
        return;
      }

      const domain = ensureDomain(domainMap, domainName, card);
      const branch = ensureBranch(domain, branchName);

      domain.order = Math.min(domain.order, card.order);
      branch.order = Math.min(branch.order, card.order);
      branch.sections.push(card);
    }

    function ensureDomain(domainMap, domainName, card) {
      if (!domainMap.has(domainName)) {
        domainMap.set(domainName, {
          name: domainName,
          type: card.type,
          order: Number.POSITIVE_INFINITY,
          card: null,
          branchMap: new Map(),
          branches: [],
        });
      }

      return domainMap.get(domainName);
    }

    function ensureBranch(domain, branchName) {
      if (!domain.branchMap.has(branchName)) {
        domain.branchMap.set(branchName, {
          name: branchName,
          order: Number.POSITIVE_INFINITY,
          card: null,
          sections: [],
        });
      }

      return domain.branchMap.get(branchName);
    }

    function getSortedDomains(domainMap) {
      return Array.from(domainMap.values())
          .map((domain) => {
            return {
              ...domain,
              branches: getSortedBranches(domain.branchMap),
            };
          })
          .filter((domain) => domain.card || domain.branches.length > 0)
          .sort(sortByOrder);
    }

    function getSortedBranches(branchMap) {
      return Array.from(branchMap.values())
          .map((branch) => {
            return {
              ...branch,
              sections: [...branch.sections].sort(sortByOrder),
            };
          })
          .filter((branch) => branch.card || branch.sections.length > 0)
          .sort(sortByOrder);
    }

    function getDomainName(card) {
      return card.domain || card.title || "";
    }

    function getBranchName(card) {
      return card.branch || card.title || "";
    }

    function getBranchGridStyle(domain) {
      return {
        "--branch-columns": String(Math.max(1, Math.min(domain.branches.length, 2))),
      };
    }

    function getDomainClasses(domain) {
      return {
        [`reference-parameters__domain_tone_${domain.type}`]: Boolean(domain.type),
      };
    }

    function hasDomainChildren(domain) {
      return domain.branches.some((branch) => {
        return Boolean(branch.card) || branch.sections.length > 0;
      });
    }

    function getLineSideByDomainIndex(domainIndex) {
      return domainIndex === 0 ? "right" : "left";
    }

    function getNodeSideClasses(side = "right") {
      return {
        "reference-parameters__node_line_from-right": side === "right",
        "reference-parameters__node_line_from-left": side === "left",
      };
    }

    function getDomainNodeClasses(domain, domainIndex) {
      const hasLine =
          hasVisibleDomainCards.value &&
          Boolean(totalCard.value) &&
          Boolean(domain.card) &&
          hasDomainChildren(domain);

      return {
        "reference-parameters__node_has-line": hasLine,
        "reference-parameters__node_line_total": hasLine,
        ...getNodeSideClasses(getLineSideByDomainIndex(domainIndex)),
      };
    }

    function getBranchNodeClasses(domain, branch, domainIndex) {
      const hasLine =
          hasVisibleDomainCards.value &&
          Boolean(domain.card) &&
          Boolean(branch.card);

      return {
        "reference-parameters__node_has-line": hasLine,
        "reference-parameters__node_line_domain": hasLine,
        ...getNodeSideClasses(getLineSideByDomainIndex(domainIndex)),
      };
    }

    function getSectionNodeClasses(domain, branch, sectionIndex, domainIndex) {
      const hasLine =
          hasVisibleDomainCards.value &&
          sectionIndex === 0 &&
          (Boolean(branch.card) || Boolean(domain.card));

      const lineClass = branch.card
          ? "reference-parameters__node_line_branch"
          : "reference-parameters__node_line_domain";

      return {
        "reference-parameters__node_has-line": hasLine,
        [lineClass]: hasLine,
        ...getNodeSideClasses(getLineSideByDomainIndex(domainIndex)),
      };
    }

    return {
      totalCard,
      visibleDomains,
      getBranchGridStyle,
      getDomainClasses,
      getDomainNodeClasses,
      getBranchNodeClasses,
      getSectionNodeClasses,
    };
  },
};
</script>

<style lang="scss" scoped>
.reference-parameters {
  margin-top: 12px;
  border-radius: 7px;
  overflow: hidden;
}

.reference-parameters__top {
  margin-bottom: 28px;
}

.reference-parameters__title {
  margin: 0 0 16px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.reference-parameters__description {
  margin: 0 0 16px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);

  p {
    margin: 0;
  }

  ol {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin: 0;
    padding-left: 2px;
  }
}

.reference-parameters__switch {
  display: inline-flex;
  align-items: center;
  column-gap: 8px;
  cursor: pointer;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.reference-parameters__scheme {
  width: 100%;
  max-width: 980px;
  margin: 0 auto 32px;

  --tree-line: #e8eeee;
  --line-side-offset: 20px;

  --domain-gap-x: 20px;
  --row-gap: 16px;

  --total-width: 320px;
  --total-height: 85px;

  --domain-width: 320px;
  --domain-height: 85px;

  --branch-width: 227px;
  --branch-height: 119px;

  --section-width: 227px;
  --section-height: 119px;

  --domain-area-width: calc((var(--branch-width) * 2) + var(--domain-gap-x));

  --total-to-domain-gap: 16px;
  --domain-to-branch-gap: 16px;

  --line-total-height: var(--total-to-domain-gap);
  --line-domain-height: var(--domain-to-branch-gap);
  --line-branch-height: var(--row-gap);
}

.reference-parameters__total {
  display: flex;
  justify-content: center;
  margin-bottom: var(--total-to-domain-gap);
}

.reference-parameters__total-card {
  position: relative;
  width: var(--total-width);
  min-height: var(--total-height);
}

.reference-parameters__domains {
  display: grid;
  grid-template-columns: repeat(2, var(--domain-area-width));
  justify-content: center;
  align-items: start;
  column-gap: var(--domain-gap-x);
}

.reference-parameters__domain {
  width: var(--domain-area-width);
  min-width: 0;
}

.reference-parameters__domain_tone_experienced {
  grid-column: 1;
  grid-row: 1;
}

.reference-parameters__domain_tone_strategic {
  grid-column: 2;
  grid-row: 1;
}

.reference-parameters__domain-row {
  display: flex;
  width: 100%;
  margin-bottom: var(--domain-to-branch-gap);
}

.reference-parameters__domain_tone_experienced .reference-parameters__domain-row {
  justify-content: flex-end;
}

.reference-parameters__domain_tone_strategic .reference-parameters__domain-row {
  justify-content: flex-start;
}

.reference-parameters__node {
  position: relative;
  min-width: 0;
}

.reference-parameters__node_has-line {
  &::before {
    content: "";
    position: absolute;
    top: calc(var(--current-line-height) * -1);
    width: 1px;
    height: var(--current-line-height);
    background: var(--tree-line);
    pointer-events: none;
    z-index: 0;
  }
}

.reference-parameters__node_line_from-right {
  &::before {
    right: var(--line-side-offset);
  }
}

.reference-parameters__node_line_from-left {
  &::before {
    left: var(--line-side-offset);
  }
}

.reference-parameters__node_line_total {
  --current-line-height: var(--line-total-height);
}

.reference-parameters__node_line_domain {
  --current-line-height: var(--line-domain-height);
}

.reference-parameters__node_line_branch {
  --current-line-height: var(--line-branch-height);
}

.reference-parameters__node_domain {
  width: var(--domain-width);
  min-height: var(--domain-height);
}

.reference-parameters__node_branch {
  width: var(--branch-width);
  min-height: var(--branch-height);
}

.reference-parameters__node_section {
  width: var(--section-width);
  min-height: var(--section-height);
}

.reference-parameters__branches {
  display: grid;
  grid-template-columns: repeat(var(--branch-columns), var(--branch-width));
  justify-content: center;
  align-items: start;
  column-gap: var(--domain-gap-x);
}

.reference-parameters__branch-column {
  width: var(--branch-width);
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--row-gap);
}

.reference-parameters__sections {
  display: flex;
  flex-direction: column;
  gap: var(--row-gap);
}

.reference-parameters__card {
  width: 100%;
  height: 100%;
  min-height: inherit;
}

@media (max-width: $tablet-breakpoint) {
  .reference-parameters__top {
    margin-bottom: 20px;
  }

  .reference-parameters__scheme {
    max-width: 100%;

    --domain-gap-x: 20px;
    --row-gap: 16px;

    --total-width: min(100%, 320px);
    --total-height: 85px;

    --domain-width: 100%;
    --domain-height: 85px;

    --branch-width: calc(100% - 20px);
    --branch-height: 119px;

    --section-width: calc(100% - 52px);
    --section-height: 119px;

    --domain-area-width: minmax(272px, 1fr);
  }

  .reference-parameters__domains {
    grid-template-columns: repeat(2, minmax(272px, 1fr));
  }

  .reference-parameters__domain {
    width: 100%;
  }

  .reference-parameters__domain-row {
    justify-content: center !important;
  }

  .reference-parameters__node_domain {
    width: var(--domain-width);
    min-width: 272px;
  }

  .reference-parameters__branches {
    grid-template-columns: 1fr;
    justify-items: end;
    gap: 16px;
  }

  .reference-parameters__branch-column {
    width: 100%;
    align-items: flex-end;
  }

  .reference-parameters__node_branch {
    width: var(--branch-width);
    min-width: 252px;
  }

  .reference-parameters__sections {
    width: var(--section-width);
    min-width: 220px;
    margin-left: auto;
    margin-right: 0;
  }

  .reference-parameters__node_section {
    width: 100%;
  }

  .reference-parameters__node_line_domain,
  .reference-parameters__node_line_branch {
    &::before {
      display: none;
    }
  }
}

@media (max-width: $mobile-breakpoint) {
  .reference-parameters__scheme {
    max-width: 100%;

    --domain-gap-x: 20px;
    --row-gap: 16px;

    --total-width: 100%;
    --total-height: 85px;

    --domain-width: 100%;
    --domain-height: 85px;

    --branch-width: calc(100% - 20px);
    --branch-height: 85px;

    --section-width: calc(100% - 52px);
    --section-height: 102px;

    --domain-area-width: 100%;
  }

  .reference-parameters__total {
    margin-bottom: 16px;
  }

  .reference-parameters__total-card {
    width: var(--total-width);
  }

  .reference-parameters__domains {
    grid-template-columns: 1fr;
    row-gap: 16px;
  }

  .reference-parameters__domain_tone_experienced,
  .reference-parameters__domain_tone_strategic {
    grid-column: auto;
    grid-row: auto;
  }
  
  .reference-parameters__domain-row {
    justify-content: center !important;
  }

  .reference-parameters__branches {
    grid-template-columns: 1fr;
    justify-items: end;
  }

  .reference-parameters__branch-column {
    width: 100%;
    align-items: flex-end;
  }

  .reference-parameters__node_domain {
    width: var(--domain-width);
    min-width: 0;
  }

  .reference-parameters__node_branch {
    width: var(--branch-width);
    min-width: 0;
  }

  .reference-parameters__sections {
    width: var(--section-width);
    min-width: 0;
    margin-left: auto;
    margin-right: 0;
  }

  .reference-parameters__node_section {
    width: 100%;
  }

  .reference-parameters__node_has-line {
    &::before {
      display: none;
    }
  }

  .reference-parameters__staffs {
    width: 100%;
  }
}
</style>